import { useState, useEffect } from 'react'
import { Modal, Button, Input, Divider, Popconfirm, Typography, Spin, Tag, theme, message } from 'antd'
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons'
import {
  useRdaIntervento,
  useCreateRda,
  useAddRdaItem,
  useDeleteRdaItem,
  useUpdateRda,
} from '@/hooks/useRdaIntervento'
import VoiceTextarea from '@/components/VoiceTextarea'
import axiosInstance from '@/lib/axios'
import type { RdoTemplate } from '@/hooks/useRdoTemplates'

const { Text } = Typography

interface Props {
  open: boolean
  onClose: () => void
  interventionId: number
  workOrderId: number
  interventionInfo: {
    descrizione: string
    lavorazioneNome?: string | null
    note?: string | null
  }
}

export default function RdaInterventoModal({
  open,
  onClose,
  interventionId,
  workOrderId,
  interventionInfo,
}: Props) {
  const { token } = theme.useToken()
  const [nuovoArticolo, setNuovoArticolo] = useState('')
  const [noteLocale, setNoteLocale] = useState('')

  const { data: rda, isLoading } = useRdaIntervento(open ? interventionId : undefined)
  const createRda = useCreateRda()
  const addItem = useAddRdaItem()
  const deleteItem = useDeleteRdaItem()
  const updateRda = useUpdateRda()

  // Sync nota locale dalla RDA quando caricata
  useEffect(() => {
    setNoteLocale(rda?.note ?? '')
  }, [rda?.note])

  // Auto-save nota dopo 800ms di inattività
  useEffect(() => {
    if (!rda) return
    if (noteLocale.trim() === (rda.note ?? '').trim()) return
    const t = setTimeout(() => {
      updateRda.mutate({ rdaId: rda.id, note: noteLocale.trim() })
    }, 800)
    return () => clearTimeout(t)
  }, [noteLocale]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleAggiungi() {
    const testo = nuovoArticolo.trim()
    if (!testo) return

    let rdaId = rda?.id
    if (!rdaId) {
      const newRda = await createRda.mutateAsync({
        intervention_id: interventionId,
        work_order_id: workOrderId,
      })
      rdaId = newRda.id
    }

    // Cerca se il testo corrisponde a una parola breve template
    try {
      const res = await axiosInstance.get<RdoTemplate>('/rdo-templates/match', { params: { q: testo } })
      const tmpl = res.data
      const items = tmpl.items.slice().sort((a, b) => a.ordine - b.ordine)
      for (const it of items) {
        await addItem.mutateAsync({ rdaId, descrizione: it.descrizione })
      }
      message.success(`Template "${tmpl.nome}" applicato — ${items.length} articoli aggiunti`)
    } catch (err: any) {
      if (err?.response?.status === 404) {
        // Nessun template trovato: aggiunge articolo normale
        await addItem.mutateAsync({ rdaId, descrizione: testo })
      } else {
        // Errore API (es. permessi, rete): aggiunge comunque ma logga
        console.warn('RDO template match error:', err?.response?.status, err?.message)
        await addItem.mutateAsync({ rdaId, descrizione: testo })
      }
    }

    setNuovoArticolo('')
  }

return (
    <Modal
      title="Gestione RDA — Articoli da acquistare"
      open={open}
      onCancel={onClose}
      width={540}
      footer={[
        <Button key="close" onClick={onClose}>
          Chiudi
        </Button>,
      ]}
    >
      {/* Header read-only */}
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ display: 'block', fontSize: 14 }}>
          {interventionInfo.descrizione}
        </Text>
        {interventionInfo.lavorazioneNome && (
          <Tag
            style={{
              background: token.colorInfoBg,
              border: `1px solid ${token.colorInfoBorder}`,
              color: token.colorInfo,
              marginTop: 4,
            }}
          >
            {interventionInfo.lavorazioneNome}
          </Tag>
        )}
        {interventionInfo.note && (
          <Text
            type="secondary"
            style={{ display: 'block', fontStyle: 'italic', fontSize: 12, marginTop: 4 }}
          >
            {interventionInfo.note}
          </Text>
        )}
      </div>

      {/* Nota RDA — visibile solo se RDA esiste */}
      {rda && (
        <div style={{ marginBottom: 12 }}>
          <VoiceTextarea
            value={noteLocale}
            onChange={setNoteLocale}
            rows={2}
            minHeight={60}
            maxHeight={160}
            placeholder="Note sull'acquisto (salvate automaticamente)..."
            label="Note RDA"
            compact
          />
        </div>
      )}

      <Divider style={{ margin: '12px 0' }}>Articoli da acquistare</Divider>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <>
          {/* Lista articoli */}
          {rda && rda.items.length > 0 ? (
            <div style={{ marginBottom: 8 }}>
              {rda.items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 0',
                    borderBottom: '1px solid #f5f5f5',
                  }}
                >
                  <Text style={{ fontSize: 13 }}>{item.descrizione}</Text>
                  <Popconfirm
                    title="Eliminare questo articolo?"
                    okText="Sì"
                    cancelText="No"
                    onConfirm={() => deleteItem.mutate({ rdaId: rda.id, itemId: item.id })}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </div>
              ))}
            </div>
          ) : (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
              Nessun articolo ancora. Aggiungi il primo.
            </Text>
          )}

          {/* Form aggiunta */}
          <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            <Input
              value={nuovoArticolo}
              onChange={(e) => setNuovoArticolo(e.target.value)}
              placeholder="Descrivi cosa acquistare..."
              onPressEnter={handleAggiungi}
            />
            <Button
              icon={<SaveOutlined />}
              onClick={handleAggiungi}
              loading={addItem.isPending || createRda.isPending}
              title="Salva articolo"
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAggiungi}
              disabled={addItem.isPending || createRda.isPending}
              title="Aggiungi articolo"
            />
          </div>
        </>
      )}
    </Modal>
  )
}
